import React, { Fragment, useEffect } from "react";
import { useForm } from "react-hook-form";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import Spinner from "../FieldComponets/Spinner";
import { ErrorMessage } from "@hookform/error-message";
import { useDispatch, useSelector } from "react-redux";
import ToastDefault from "../FieldComponets/Toast";
import PageLayout from "../Layouts/PageLayout";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { decodeID, getLastURL } from "../../utils/Helper";
import {
  SubscriptionPlanDetailsAction,
  SubscriptionPlanUpdateAction,
} from "../../Redux/Action/SubscriptionPlanAction";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";

type FormInputs = {
  name: string;
  overview: string;
  price: number;
  validity_days: number;
  max_services: string;
  max_staff: string;
  max_offline_booking_staff: string;
  enable_growth_statistics: boolean;
  enable_appointment_cancellation: boolean;
};

function SubscriptionPlanEdit() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const intl = useIntl();
  const plan = useSelector((state: any) => state.SubscriptionPlanDetailsState?.data?.data);
  const updateState = useSelector((state: any) => state.SubscriptionPlanUpdateState);
  const log_in = useSelector((state: any) => state.AdminLogInState);
  const location = useLocation();
  const editId = getLastURL(location);

  const formSchema = Yup.object({
    name: Yup.string()
      .required(intl.formatMessage({ id: "please_enter_subscription_name" }))
      .trim()
      .max(255),
    overview: Yup.string().default(""),
    price: Yup.number()
      .typeError(intl.formatMessage({ id: "price_must_be_number" }))
      .min(0, intl.formatMessage({ id: "price_cannot_be_negative" }))
      .required(intl.formatMessage({ id: "please_enter_price" })),
    validity_days: Yup.number()
      .typeError(intl.formatMessage({ id: "validity_must_be_number" }))
      .integer(intl.formatMessage({ id: "use_whole_days" }))
      .min(1, intl.formatMessage({ id: "at_least_1_day" }))
      .required(intl.formatMessage({ id: "please_enter_validity" })),
    max_services: Yup.string().default(""),
    max_staff: Yup.string().default(""),
    max_offline_booking_staff: Yup.string().default(""),
    enable_growth_statistics: Yup.boolean().default(true),
    enable_appointment_cancellation: Yup.boolean().default(true),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FormInputs>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: "",
      overview: "",
      price: 0,
      validity_days: 30,
      max_services: "",
      max_staff: "",
      max_offline_booking_staff: "",
      enable_growth_statistics: true,
      enable_appointment_cancellation: true,
    },
  });

  const planCode = plan?.plan_code;

  useEffect(() => {
    dispatch(
      SubscriptionPlanDetailsAction({
        detail_id: decodeID(editId),
      }),
    );
  }, [dispatch, editId]);

  useEffect(() => {
    if (plan?.id) {
      reset({
        name: plan.name,
        overview: plan.overview || "",
        price: Number(plan.price),
        validity_days: plan.validity_days,
        max_services:
          plan.max_services != null ? String(plan.max_services) : "",
        max_staff: plan.max_staff != null ? String(plan.max_staff) : "",
        max_offline_booking_staff:
          plan.max_offline_booking_staff != null
            ? String(plan.max_offline_booking_staff)
            : "",
        enable_growth_statistics: !!plan.enable_growth_statistics,
        enable_appointment_cancellation: !!plan.enable_appointment_cancellation,
      });
    }
  }, [plan, reset]);

  useEffect(() => {
    if (updateState.status) {
      setTimeout(() => navigate("/subscription-plans"), 800);
    }
  }, [updateState.status, navigate]);

  useEffect(() => {
    return () => {
      dispatch(SubscriptionPlanUpdateAction("RESET"));
    };
  }, [dispatch]);

  const onSubmit = (data: FormInputs) => {
    if (!plan?.id) return;

    const checkPositiveOrEmpty = (raw: string, label: string): boolean => {
      const t = String(raw ?? "").trim();
      if (t === "") return true;
      const n = parseInt(t, 10);
      if (!Number.isFinite(n) || n < 1) {
        toast.error(intl.formatMessage({ id: "enter_positive_number_or_unlimited" }, { label }));
        return false;
      }
      return true;
    };

    if (planCode === "basic") {
      const ms = parseInt(String(data.max_services).trim(), 10);
      const mst = parseInt(String(data.max_staff).trim(), 10);
      const mob = parseInt(String(data.max_offline_booking_staff).trim(), 10);
      if (!ms || ms < 1) {
        toast.error(intl.formatMessage({ id: "please_enter_positive_max_services_basic" }));
        return;
      }
      if (!mst || mst < 1) {
        toast.error(intl.formatMessage({ id: "please_enter_positive_max_staff_basic" }));
        return;
      }
      if (!mob || mob < 1) {
        toast.error(intl.formatMessage({ id: "please_enter_positive_offline_booking_staff_basic" }));
        return;
      }
    } else if (planCode === "premium") {
      if (
        !checkPositiveOrEmpty(data.max_services, intl.formatMessage({ id: "max_services_label" })) ||
        !checkPositiveOrEmpty(data.max_staff, intl.formatMessage({ id: "max_staff_label" })) ||
        !checkPositiveOrEmpty(
          data.max_offline_booking_staff,
          intl.formatMessage({ id: "max_staff_offline_bookings_label" }),
        )
      ) {
        return;
      }
    }

    const payload: any = {
      edit_id: plan.id,
      name: data.name,
      overview: data.overview,
      price: data.price,
      validity_days: data.validity_days,
      max_services: data.max_services,
      max_staff: data.max_staff,
      max_offline_booking_staff: data.max_offline_booking_staff,
      enable_growth_statistics: data.enable_growth_statistics ? 1 : 0,
      enable_appointment_cancellation: data.enable_appointment_cancellation ? 1 : 0,
    };
    dispatch(SubscriptionPlanUpdateAction(payload));
  };

  return (
    <Fragment>
      {log_in?.loading ? <Spinner /> : ""}
      <PageLayout
        title={intl.formatMessage({ id: "edit_subscription_plan" })}
        breadcrumbs={{
          secLast: { link: "/subscription-plans", name: intl.formatMessage({ id: "subscription_plans" }) },
        }}
        buttons={[{ url: "", className: "", text: "", show: false }]}
      >
        <div className="mt-2">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row gy-4 mb-2">
              <div className="col-md-6">
                <label className="form-label"><FormattedMessage id="subscription_name" /></label>
                <input type="text" className="form-control mb-0" {...register("name")} />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => (
                    <p className="mt-1 mb-0" style={{ color: "red" }}>
                      {message}
                    </p>
                  )}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label"><FormattedMessage id="plan_type_label" /></label>
                <input
                  type="text"
                  className="form-control mb-0"
                  readOnly
                  disabled
                  value={
                    plan?.plan_code === "basic"
                      ? intl.formatMessage({ id: "basic_plan" })
                      : plan?.plan_code === "premium"
                        ? intl.formatMessage({ id: "premium_plan" })
                        : "—"
                  }
                />
              </div>
              <div className="col-12">
                <label className="form-label"><FormattedMessage id="subscription_overview" /></label>
                <textarea className="form-control" rows={3} {...register("overview")} />
              </div>
              <div className="col-md-4">
                <label className="form-label"><FormattedMessage id="subscription_price" /></label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control mb-0"
                  {...register("price", { valueAsNumber: true })}
                />
                <ErrorMessage
                  errors={errors}
                  name="price"
                  render={({ message }) => (
                    <p className="mt-1 mb-0" style={{ color: "red" }}>
                      {message}
                    </p>
                  )}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label"><FormattedMessage id="validity_days" /></label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  {...register("validity_days", { valueAsNumber: true })}
                />
                <ErrorMessage
                  errors={errors}
                  name="validity_days"
                  render={({ message }) => (
                    <p className="mt-1 mb-0" style={{ color: "red" }}>
                      {message}
                    </p>
                  )}
                />
              </div>
            </div>

            <h6 className="mt-3"><FormattedMessage id="configurable_limits" /></h6>
            <p className="small text-muted">
              <FormattedMessage
                id="plan_limits_note"
                values={{ b: (chunks: React.ReactNode) => <strong>{chunks}</strong> }}
              />
            </p>

            <div className="row gy-4 mb-2">
              <div className="col-md-4">
                <label className="form-label"><FormattedMessage id="max_services_label" /></label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  placeholder={planCode === "premium" ? intl.formatMessage({ id: "empty_unlimited" }) : ""}
                  {...register("max_services")}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label"><FormattedMessage id="max_staff_members_label" /></label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  placeholder={planCode === "premium" ? intl.formatMessage({ id: "empty_unlimited" }) : ""}
                  {...register("max_staff")}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label"><FormattedMessage id="max_staff_offline_bookings_label" /></label>
                <input
                  type="number"
                  min={1}
                  className="form-control mb-0"
                  placeholder={planCode === "premium" ? intl.formatMessage({ id: "empty_unlimited" }) : ""}
                  {...register("max_offline_booking_staff")}
                />
              </div>
            </div>

            <div className="row gy-3 mb-4">
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="growth"
                    {...register("enable_growth_statistics")}
                  />
                  <label className="form-check-label" htmlFor="growth">
                    <FormattedMessage id="enable_growth_revenue_statistics" />
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="cancel"
                    {...register("enable_appointment_cancellation")}
                  />
                  <label className="form-check-label" htmlFor="cancel">
                    <FormattedMessage id="enable_cancel_upcoming_appointments" />
                  </label>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary">
              <FormattedMessage id="save" />
            </button>
          </form>
        </div>
        <ToastDefault />
      </PageLayout>
    </Fragment>
  );
}

export default SubscriptionPlanEdit;
